import type { ColumnDef, ColumnSizingState } from '@tanstack/react-table'
import type { Task } from '@/types'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { formatDate, isPastDate } from '@/lib/utils'
import { ACTIVITY_TYPE_COLORS, CATEGORICAL, GRACKLE_COLORS } from '@/lib/theme'
import { CheckCircle2, Clock, AlertCircle, Phone, Mail, Calendar, Circle } from 'lucide-react'

const getRelatedToPath = (id: string): string => {
  if (id.startsWith('006')) return `/opportunities/${id}`
  if (id.startsWith('001')) return `/accounts/${id}`
  if (id.startsWith('003')) return `/contacts/${id}`
  if (id.startsWith('00Q')) return `/leads/${id}`
  return '#'
}

const getPriorityVariant = (priority?: string): 'default' | 'warning' | 'destructive' => {
  switch (priority?.toLowerCase()) {
    case 'high':
      return 'destructive'
    case 'medium':
      return 'warning'
    default:
      return 'default'
  }
}

// Contrast-safe (WCAG AA) text colors for the status, mirroring the
// account-level activity timeline semantics. Note: `--warning` (Pollen yellow)
// is a badge *background* token and fails as text on white, so "Not Started"
// uses an explicit amber that passes in both light and dark mode.
const getStatusColorClass = (status: string, isOverdue: boolean): string => {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'text-success'
    case 'in progress':
      return 'text-info'
    case 'not started':
      // Overdue = Raspberry (destructive token). Non-overdue "not started" is
      // neutral text — the warning hue (Pollen) fails as text on white.
      return isOverdue ? 'text-destructive' : 'text-muted-foreground'
    default:
      return 'text-muted-foreground'
  }
}

const getStatusIcon = (status: string, colorClass: string) => {
  const className = `h-4 w-4 ${colorClass}`
  switch (status.toLowerCase()) {
    case 'completed':
      return <CheckCircle2 className={className} />
    case 'in progress':
      return <Clock className={className} />
    default:
      return <AlertCircle className={className} />
  }
}

const getTaskTypeIcon = (type?: string) => {
  switch (type) {
    case 'Call':
      return (
        <Phone
          className="h-3.5 w-3.5"
          style={{ color: ACTIVITY_TYPE_COLORS.Call.solid }}
          aria-label="Call"
        />
      )
    case 'Email':
      return (
        <Mail
          className="h-3.5 w-3.5"
          style={{ color: ACTIVITY_TYPE_COLORS.Email.solid }}
          aria-label="Email"
        />
      )
    case 'Meeting':
      return (
        <Calendar
          className="h-3.5 w-3.5"
          style={{ color: ACTIVITY_TYPE_COLORS.Event.solid }}
          aria-label="Meeting"
        />
      )
    case 'Other':
      return (
        <Circle
          className="h-3.5 w-3.5"
          style={{ color: CATEGORICAL.slate.solid }}
          aria-label="Other"
        />
      )
    default:
      return null
  }
}

interface TasksColumnsProps {
  onViewTask: (taskId: string, subject: string) => void
  selectedIds?: Set<string>
  onToggleSelect?: (id: string) => void
  onToggleSelectAll?: () => void
  allSelected?: boolean
  /** One-click complete/reopen from the status icon (non-RFP tasks only). */
  onToggleComplete?: (task: Task) => void
}

export const createTasksColumns = ({
  onViewTask,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  allSelected,
  onToggleComplete,
}: TasksColumnsProps): ColumnDef<Task>[] => [
  {
    id: 'select',
    header: () => (
      <Checkbox
        checked={allSelected}
        onCheckedChange={onToggleSelectAll}
        aria-label="Select all tasks"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={selectedIds?.has(row.original.id) ?? false}
        onCheckedChange={() => onToggleSelect?.(row.original.id)}
        aria-label={`Select task ${row.original.subject}`}
      />
    ),
    size: 50,
    minSize: 50,
    maxSize: 50,
    enableResizing: false,
  },
  {
    id: 'subject',
    header: 'Subject',
    accessorKey: 'subject',
    meta: { sortField: 'subject' },
    size: 300,
    cell: ({ row }) => (
      <div className="flex items-center gap-1.5">
        {getTaskTypeIcon(row.original.type)}
        {row.original.task_source === 'rfp_task' && (
          <span
            className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold shrink-0"
            style={{ backgroundColor: CATEGORICAL.plum.tint, color: GRACKLE_COLORS.prussianBlue }}
            aria-label="RFP task"
            title="This task was created in the RFP module"
          >
            RFP
          </span>
        )}
        <Button
          variant="link"
          className="p-0 h-auto font-medium text-left"
          onClick={() => onViewTask(row.original.id, row.original.subject)}
          aria-label={
            row.original.task_source === 'rfp_task'
              ? `Open RFP for task: ${row.original.subject}`
              : `View task: ${row.original.subject}`
          }
        >
          {row.original.subject}
        </Button>
      </div>
    ),
  },
  {
    id: 'who_name',
    header: 'Name',
    size: 180,
    cell: ({ row }) => {
      const whoId = row.original.who_id
      const whoName = row.original.who_name
      if (!whoId || !whoName) return <span className="text-muted-foreground">—</span>
      return (
        <Link
          to={getRelatedToPath(whoId)}
          className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          aria-label={`View ${whoId.startsWith('00Q') ? 'lead' : 'contact'}: ${whoName}`}
        >
          {whoName}
        </Link>
      )
    },
  },
  {
    id: 'related_to',
    header: 'Related To',
    size: 200,
    cell: ({ row }) => {
      // Prefer the related object (account / opportunity / ticket). When there
      // is no resolvable object (e.g. a lead-associated task, whose what_id is
      // a placeholder that resolves to no name), fall back to the associated
      // lead/contact (the "who") so the column still links somewhere useful.
      const whatId = row.original.what_id
      const whatName = row.original.what_name
      const relatedId = whatName ? whatId : row.original.who_id
      const relatedName = whatName ? whatName : row.original.who_name
      if (!relatedId || !relatedName) return <span className="text-muted-foreground">—</span>
      return (
        <Link
          to={getRelatedToPath(relatedId)}
          className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          aria-label={`View related: ${relatedName}`}
        >
          {relatedName}
        </Link>
      )
    },
  },
  {
    id: 'due_date',
    header: 'Due Date',
    accessorKey: 'due_date',
    meta: { sortField: 'activity_date' },
    size: 150,
    cell: ({ row }) => {
      const dueDate = row.original.due_date
      if (!dueDate) return 'No due date'
      const isOverdue = isPastDate(dueDate) && row.original.status?.toLowerCase() !== 'completed'
      return (
        <div className="flex items-center gap-2">
          <span className={isOverdue ? 'text-destructive font-medium' : ''}>
            {formatDate(dueDate)}
          </span>
          {isOverdue && (
            <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
              Overdue
            </Badge>
          )}
        </div>
      )
    },
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    meta: { sortField: 'status' },
    size: 130,
    cell: ({ row }) => {
      const task = row.original
      const isRfp = task.task_source === 'rfp_task'
      const isCompleted = task.status === 'Completed'
      const isOverdue = isPastDate(task.due_date) && task.status?.toLowerCase() !== 'completed'
      const colorClass = getStatusColorClass(task.status, isOverdue)
      return (
        <div className="flex items-center gap-1.5">
          {onToggleComplete && !isRfp ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onToggleComplete(task)
              }}
              className="cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label={
                isCompleted ? `Reopen task: ${task.subject}` : `Mark complete: ${task.subject}`
              }
              title={isCompleted ? 'Click to reopen' : 'Click to mark complete'}
            >
              {getStatusIcon(task.status, colorClass)}
            </button>
          ) : (
            getStatusIcon(task.status, colorClass)
          )}
          <span className={`text-sm font-medium ${colorClass}`}>{task.status}</span>
        </div>
      )
    },
  },
  {
    id: 'priority',
    header: 'Priority',
    accessorKey: 'priority',
    meta: { sortField: 'priority' },
    size: 120,
    cell: ({ row }) =>
      row.original.priority ? (
        <Badge variant={getPriorityVariant(row.original.priority)}>{row.original.priority}</Badge>
      ) : null,
  },
  {
    id: 'owner_name',
    header: 'Assigned To',
    accessorKey: 'owner_name',
    meta: { sortField: 'owner_name' },
    size: 150,
    cell: ({ row }) => (
      <span className="truncate block max-w-[150px]">{row.original.owner_name || '-'}</span>
    ),
  },
  {
    id: 'last_modified_date',
    header: 'Last Modified',
    accessorKey: 'last_modified_date',
    meta: { sortField: 'last_modified_date' },
    size: 140,
    cell: ({ row }) =>
      (row.original as any).last_modified_date
        ? formatDate((row.original as any).last_modified_date)
        : '-',
  },
]

export const tasksDefaultColumnSizing: ColumnSizingState = {
  select: 50,
  subject: 250,
  who_name: 180,
  related_to: 200,
  due_date: 150,
  status: 130,
  priority: 120,
  owner_name: 150,
  last_modified_date: 140,
}
